import React, { useState, useContext, useEffect, useMemo } from "react";
import Equipments from "./Equipments";
import { IoMdCheckmark } from "react-icons/io";
import DefaultFee from "./DefaultFee";
import { Grid, Stack } from "@mui/material";
import CurrencyInput from "./input-elements/CurrencyInput";
import { EquipmentContext } from "../useContext/context";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import pricingValidation from "../pricing-validation";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import PercentageInput from "./input-elements/PercentageInput";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import ACHPricing from "./ACHPricing";

const dual_pricing_validation =
    pricingValidation.pricingValidation.dual_pricing;
    
const rates = ["3", "3.25", "3.5", "3.75", "4"];

const DualPricing = ({
    selectedEquipments,
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
    isEditable = true,
}) => {
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);
    const [dualPricingDefaults, setDualPricingDefaults] = useState(dual_pricing_validation);

    // Update dual pricing defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.dual_pricing) {
            setDualPricingDefaults(pricingDefaults.dual_pricing);
        }
    }, [pricingDefaults]);

    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            dual_pricing_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Dual Pricing Rate is required")
                .min(
                    dualPricingDefaults.dual_pricing_rate?.minimum || dual_pricing_validation.dual_pricing_rate.minimum,
                    `Please enter a rate of ${(dualPricingDefaults.dual_pricing_rate?.minimum || dual_pricing_validation.dual_pricing_rate.minimum).toFixed(2)}% or higher.`,
                ),
                
            dual_pricing_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Per Transaction fee is required")
                .min(
                    dualPricingDefaults.dual_pricing_per_transaction?.minimum || dual_pricing_validation.dual_pricing_per_transaction.minimum,
                    `Please enter a fee of $${(dualPricingDefaults.dual_pricing_per_transaction?.minimum || dual_pricing_validation.dual_pricing_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_monthly_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Monthly Fee is required")
                .min(
                    dualPricingDefaults.dual_pricing_monthly_fee?.minimum || dual_pricing_validation.dual_pricing_monthly_fee.minimum,
                    `Please enter a fee of $${(dualPricingDefaults.dual_pricing_monthly_fee?.minimum || dual_pricing_validation.dual_pricing_monthly_fee.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_pin_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Rate is required")
                .min(
                    dualPricingDefaults.dual_pricing_pin_debit_rate?.minimum || dual_pricing_validation.dual_pricing_pin_debit_rate.minimum,
                    `Please enter a rate of ${(dualPricingDefaults.dual_pricing_pin_debit_rate?.minimum || dual_pricing_validation.dual_pricing_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            dual_pricing_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    dualPricingDefaults.dual_pricing_pin_debit_per_transaction?.minimum || dual_pricing_validation.dual_pricing_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(dualPricingDefaults.dual_pricing_pin_debit_per_transaction?.minimum || dual_pricing_validation.dual_pricing_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            dual_pricing_pin_debit_network_fee: Yup.string().required(
                "Network Fee selection is required",
            ),

            fnf_number: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [dualPricingDefaults]);

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, { [e.target.name]: e.target.value })
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    const handleOnclick = (rate) => {
        setFormDataByKeyValue(
            "dual_pricing_rate",
            rate,
        );
    }

    return (
        <div className="dual-pricing-section mt-4r">
            <p className="sub-heading-lead">
                Select your preferred Dual Pricing rate
            </p>
            <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
                <Grid item xs={12} sm={12} md={6}>
                    <div className="pricing-model-buttons">
                        {rates.map((rate) => (
                            <button
                                disabled={!isEditable}
                                type="button"
                                key={rate}
                                className={form_data.dual_pricing_rate == parseFloat(rate) ? "active small" : "small"}
                                onClick={() => handleOnclick(rate)}
                            >
                                {parseFloat(form_data.dual_pricing_rate) == parseFloat(rate) ? <IoMdCheckmark size={18} /> : ""}{" "}
                                {rate}%
                            </button>
                        ))}
                    </div>
                    {errors?.dual_pricing_rate &&
                        Array.isArray(errors.dual_pricing_rate) &&
                        errors.dual_pricing_rate.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                    <CurrencyInput
                        className="no-margin"
                        label="Txn Fee"
                        placeholder={dualPricingDefaults.dual_pricing_per_transaction?.default}
                        value={form_data.dual_pricing_per_transaction}
                        name="dual_pricing_per_transaction"
                        type="number"
                        onChange={handleOnChange}
                        errors={errors}
                        softLimit={dualPricingDefaults?.dual_pricing_per_transaction?.soft_limit}
                    />
                    {errors?.dual_pricing_per_transaction &&
                        Array.isArray(errors.dual_pricing_per_transaction) &&
                        errors.dual_pricing_per_transaction.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                    <CurrencyInput
                        className="no-margin"
                        label="Monthly Fee"
                        placeholder={dualPricingDefaults.dual_pricing_monthly_fee?.default}
                        value={form_data.dual_pricing_monthly_fee}
                        name="dual_pricing_monthly_fee"
                        type="number"
                        onChange={handleOnChange}
                        errors={errors}
                        softLimit={dualPricingDefaults?.dual_pricing_monthly_fee?.soft_limit}
                    />
                    {errors?.dual_pricing_monthly_fee &&
                        Array.isArray(errors.dual_pricing_monthly_fee) &&
                        errors.dual_pricing_monthly_fee.map((error) => (
                            <span className="error-message">{error}</span>
                        ))}
                </Grid>
            </Grid>
            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) => {
                                setFormDataByKeyValue("dual_pricing_pin_debit_network_fee", value);
                                if (value === "no") {
                                    setFormDataByKeyValue("dual_pricing_pin_debit_rate", 0);
                                    setFormDataByKeyValue("dual_pricing_pin_debit_per_transaction", 0);
                                }
                                if (value === "yes") {
                                    setFormDataByKeyValue("dual_pricing_pin_debit_rate", dualPricingDefaults.dual_pricing_pin_debit_rate?.default ?? 0);
                                    setFormDataByKeyValue("dual_pricing_pin_debit_per_transaction", dualPricingDefaults.dual_pricing_pin_debit_per_transaction?.default ?? 0);
                                }
                            }}
                            value={form_data.dual_pricing_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="dual_pricing_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.dual_pricing_pin_debit_network_fee &&
                            Array.isArray(
                                errors.dual_pricing_pin_debit_network_fee,
                            ) &&
                            errors.dual_pricing_pin_debit_network_fee.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={dualPricingDefaults.dual_pricing_pin_debit_rate?.default}
                            name={"dual_pricing_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.dual_pricing_pin_debit_rate}
                            errors={errors}
                            softLimit={dualPricingDefaults?.dual_pricing_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.dual_pricing_pin_debit_network_fee}
                        />
                        {errors?.dual_pricing_pin_debit_rate &&
                            Array.isArray(errors.dual_pricing_pin_debit_rate) &&
                            errors.dual_pricing_pin_debit_rate.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={dualPricingDefaults.dual_pricing_pin_debit_per_transaction?.default}
                            name={"dual_pricing_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={
                                form_data.dual_pricing_pin_debit_per_transaction
                            }
                            errors={errors}
                            softLimit={dualPricingDefaults?.dual_pricing_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.dual_pricing_pin_debit_network_fee}
                        />
                        {errors?.dual_pricing_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.dual_pricing_pin_debit_per_transaction,
                            ) &&
                            errors.dual_pricing_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>
            
            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>
            <DefaultFee
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
            />
            <Equipments
                selectedEquipments={selectedEquipments}
                emsEquipments={emsEquipments}
                setFormDataByKeyValue={setFormDataByKeyValue}
                errors={errors}
                form_data={form_data}
                allEquipments={allEquipments}
                removeError={removeError}
            />
        </div>
    );
};

export default DualPricing;
